export interface Point {
  x: number;
  y: number;
}

export interface AnchorPoint {
  x: number;
  y: number;
  side: 'top' | 'bottom' | 'left' | 'right';
}

/**
 * Calculates a cubic Bezier path between two points.
 * Focused on the actual use cases in the tree layout:
 * 1. Parent→Child: right anchor to left anchor (main arrows — flows cleanly
 *    left-to-right and fans symmetrically from a centred parent)
 * 2. Parent→Archived: bottom anchor to top anchor (archived group arrows)
 * 3. Legacy bottom → left is still supported as a fallback.
 *
 * @param start - Starting point coordinates
 * @param end - Ending point coordinates
 * @param startAnchor - Which side of the start node to connect from
 * @param endAnchor - Which side of the end node to connect to
 * @param curveness - Controls how curved the path is (0-1, default 0.8)
 * @returns SVG path string for a cubic Bezier curve
 */
export const calculateBezierPath = (
  start: Point,
  end: Point,
  startAnchor: 'bottom' | 'left' | 'right' | 'top',
  endAnchor: 'left' | 'top' | 'right' | 'bottom',
  curveness: number = 0.8
): string => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;

  // Main case: right → left (parent to child).
  // A smooth horizontal S-curve that leaves the parent's right edge and enters
  // the child's left edge. Control points stay within the inter-column gap
  // (bounded at the horizontal midpoint) so the curve never bows back over a node.
  if (startAnchor === 'right' && endAnchor === 'left') {
    const cx = Math.min(Math.abs(dx) * 0.5, Math.abs(dx) * curveness);
    return `M ${start.x},${start.y} C ${start.x + cx},${start.y} ${end.x - cx},${end.y} ${end.x},${end.y}`;
  }

  // Legacy case: bottom → left (parent to child)
  // Creates a smooth curve from parent down/right to child
  if (startAnchor === 'bottom' && endAnchor === 'left') {
    // Ensure strong horizontal approach: use minimum 60px or curveness-based distance, whichever is larger
    const horizontalCurve = Math.max(60, Math.abs(dx) * curveness);

    if (dy > 0) {
      // Child is below parent — curve down from start, then right to child
      const verticalCurve = dy * curveness;
      return `M ${start.x},${start.y} C ${start.x},${start.y + verticalCurve} ${end.x - horizontalCurve},${end.y} ${end.x},${end.y}`;
    } else {
      // Child is at same level or above — curve right without dipping down
      return `M ${start.x},${start.y} C ${start.x + Math.abs(dx) * 0.4},${start.y} ${end.x - horizontalCurve},${end.y} ${end.x},${end.y}`;
    }
  }

  // Archived case: bottom → top (parent to archived group below)
  // Creates an S-curve for vertical connections
  if (startAnchor === 'bottom' && endAnchor === 'top') {
    const midY = (start.y + end.y) / 2;
    const horizontalCurve = Math.abs(dx) * curveness * 0.3;

    // Create symmetric S-curve with control points at midpoint
    return `M ${start.x},${start.y} C ${start.x + horizontalCurve},${midY} ${end.x - horizontalCurve},${midY} ${end.x},${end.y}`;
  }

  // Fallback for any other anchor combinations (shouldn't be used in practice)
  // Simple straight line as a safe default
  return `M ${start.x},${start.y} L ${end.x},${end.y}`;
};

export const getAnchorPoint = (
  nodeRect: { x: number; y: number; width: number; height: number },
  anchor: 'top' | 'bottom' | 'left' | 'right'
): Point => {
  switch (anchor) {
    case 'top':
      return { x: nodeRect.x + nodeRect.width / 2, y: nodeRect.y };
    case 'bottom':
      return { x: nodeRect.x + nodeRect.width / 2, y: nodeRect.y + nodeRect.height };
    case 'left':
      return { x: nodeRect.x, y: nodeRect.y + nodeRect.height / 2 };
    case 'right':
      return { x: nodeRect.x + nodeRect.width, y: nodeRect.y + nodeRect.height / 2 };
  }
};

export const generateArrowMarkerId = (startId: string, endId: string, variant: string = 'normal'): string => {
  return `arrow-${variant}-${startId}-${endId}`.replace(/[^a-zA-Z0-9-]/g, '');
};